import React from 'react';
import type { MatrixSelections } from '../../../services/localEmployeeGradingStore';
import type { MatrixCell, MatrixColumn, MatrixRow } from './employeeGradingEngine';
import {
  matrixCellKey,
  matrixOptionDisplayText,
  matrixRowKey,
  matrixRowLabel,
} from './employeeGradingEngine';

type GradingMatrixTableProps = {
  rows: MatrixRow[];
  columns: MatrixColumn[];
  cells: Record<string, MatrixCell>;
  selections: MatrixSelections;
  onSelect: (rowKey: string, answerId: number) => void;
  disabled?: boolean;
};

function ClusterColumnHead({ column }: { column: MatrixColumn }) {
  const rolesHint = column.roleNames.length > 0
    ? `Роли: ${column.roleNames.join(', ')}`
    : undefined;

  return (
    <span className="emp-grading-matrix-col-title">
      {column.label}
      {rolesHint && (
        <button
          type="button"
          className="emp-grading-matrix-col-hint"
          aria-label={rolesHint}
          title={rolesHint}
        >
          ?
        </button>
      )}
    </span>
  );
}

export default function GradingMatrixTable({
  rows,
  columns,
  cells,
  selections,
  onSelect,
  disabled,
}: GradingMatrixTableProps) {
  if (!rows.length) {
    return (
      <div className="emp-grading-matrix-empty">
        Для выбранного семейства пока нет дифференцирующих факторов.
      </div>
    );
  }

  if (!columns.length) {
    return (
      <div className="emp-grading-matrix-empty">
        Для выбранного семейства пока нет кластеров ролей.
      </div>
    );
  }

  return (
    <div className="emp-grading-matrix-wrap">
      <table className="emp-grading-matrix">
        <thead>
          <tr>
            <th className="emp-grading-matrix-corner">Диффактор</th>
            {columns.map((col) => (
              <th key={col.roleCluster.id} className="emp-grading-matrix-col-head">
                <ClusterColumnHead column={col} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const rowKey = matrixRowKey(row);
            const isFactorStart = rowIndex === 0 || rows[rowIndex - 1].factor.id !== row.factor.id;
            let factorBand = 0;
            for (let i = 0; i <= rowIndex; i += 1) {
              if (i === 0 || rows[i].factor.id !== rows[i - 1].factor.id) factorBand += 1;
            }
            const rowClassName = [
              'emp-grading-matrix-row',
              isFactorStart ? 'emp-grading-matrix-row-factor-start' : '',
              factorBand % 2 === 0 ? 'emp-grading-matrix-row-band-even' : 'emp-grading-matrix-row-band-odd',
            ].filter(Boolean).join(' ');

            return (
              <tr key={rowKey} className={rowClassName}>
                <th scope="row" className="emp-grading-matrix-row-head">
                  {matrixRowLabel(row)}
                </th>
                {columns.map((col) => {
                  const cell = cells[matrixCellKey(row.factor.id, row.questionIndex, col.roleCluster.id)];
                  const options = cell?.options ?? [];

                  return (
                    <td key={col.roleCluster.id} className="emp-grading-matrix-cell-wrap">
                      {options.length === 0 ? (
                        <div className="emp-grading-matrix-cell emp-grading-matrix-cell-empty">
                          <span className="emp-grading-matrix-cell-placeholder">—</span>
                        </div>
                      ) : (
                        <div className="emp-grading-matrix-cell-stack">
                          {options.map((option) => {
                            const displayText = matrixOptionDisplayText(option);
                            const selected = selections[rowKey] === option.answerId;
                            return (
                              <button
                                key={option.answerId}
                                type="button"
                                className={`emp-grading-matrix-cell-option${selected ? ' emp-grading-matrix-cell-option-selected' : ''}`}
                                onClick={() => onSelect(rowKey, option.answerId)}
                                disabled={disabled}
                                aria-pressed={selected}
                                title={
                                  displayText
                                    ?? (option.answerText ? `Короткий ответ: ${option.answerText}` : 'Нет описания для матрицы')
                                }
                              >
                                {displayText ? (
                                  <span className="emp-grading-matrix-cell-text">{displayText}</span>
                                ) : (
                                  <span className="emp-grading-matrix-cell-placeholder">—</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
